import { useState, useEffect, useContext } from "react";
import WeightEstimation from "components/weight-estimation";
import RadioGroup from "components/radio-group";
import FormContainer from "components/form-container";
import {
  checkIfFullKiosk,
  getQueryParams,
  localizeDecimalSeparator,
} from "lib/utils";
import { calculateEstimate } from "lib/calculateEstimate";
import { useOutletContext, useLocation } from "react-router-dom";
import ProgressBar from "components/progress-bar";
import { useRestartIfAbsent } from "hooks/useRestartIfAbsent";
import { useCurrentLocation } from "lib/utils";
import { useTranslation } from "react-i18next";
import { TAKEBACK_METHOD_NAME } from "consts/query-params";
import useWhen from "hooks/useWhen";
import { PAYMENT_ARGS } from "consts/feature-config";
import stores from "consts/stores";
import InputValidation from "components/input-validation";
import AuthContext from "contexts/AuthContext";
import weightRestrictions from "consts/weight-restrictions.json";
import FloodlightEvent from "components/floodlight-event";
import { EVENT_9 } from "utils/tracking";

export default function MethodPage() {
  // translation
  const { t } = useTranslation();

  // Get query params and restart flow if absent
  const location = useLocation();
  const queryParams = getQueryParams(location.search);
  useRestartIfAbsent(queryParams, ["w"], t("urls.trade_in_weight"));

  // Initialise input with query param
  const [selectedOption, setSelectedOption] = useState(
    queryParams[TAKEBACK_METHOD_NAME] || null
  );

  // Generate updated params
  const updatedParams = {
    ...queryParams,
    [TAKEBACK_METHOD_NAME]: selectedOption,
  };

  const { location: currentLocation } = useCurrentLocation();
  const currentStore = stores.find(
    (store) => store.state === currentLocation?.state
  );

  // Radio options
  const radioOptions = [
    {
      value: "mail",
      title: "Ship for free",
      subtitle:
        "Mail your LEGO bricks at your nearest UPS Location – print a shipping label or generate a shipping QR code.",
      icon: "package_2",
      iconstyle: "scale-125",
    },
    {
      value: `in_store_${currentLocation?.state?.toLowerCase()}`,
      title: `Take to the LEGO Store ${currentStore?.name}`,
      subtitle:
        "Trade in your LEGO bricks in person with the help of our Brick Specialists.",
      icon: "storefront",
      iconstyle: "scale-110",
    },
  ];

  // Filter radio options based on location methods
  let selectedRadioOptions = radioOptions;

  if (currentLocation) {
    selectedRadioOptions = radioOptions.filter((option) =>
      currentLocation?.takebackMethod?.includes(option.value)
    );
  }

  const estimate = calculateEstimate(queryParams.w);

  const parsedWeight = parseFloat(queryParams.w).toFixed(2);
  const weightEstimate =
    currentLocation?.locale !== "en-US"
      ? parsedWeight.toString().replace(".", ",")
      : parsedWeight;

  // update right side visual
  const { setSidebarVisual } = useOutletContext();

  useEffect(() => {
    setSidebarVisual("dollar");
  }, [setSidebarVisual]);

  //only show cash payment in appropriate environments
  const showPayment = useWhen(PAYMENT_ARGS);

  const { user } = useContext(AuthContext);
  const isKiosk = checkIfFullKiosk(currentLocation);

  // const useRevisedLandingPage = useWhen(END_OF_PILOT_ARGS);

  // const endOfPilotInfo = (
  //   <div className="mx-auto lg:max-w-6xl ">
  //     <div className="w-full h-full overflow-hidden relative rounded-md">
  //       <div className="h-full w-1 bg-blue absolute top-0 left-0"></div>
  //       <div className="flex flex-row justify-start bg-blue-300">
  //         <span
  //           className={`material-symbols-outlined scale-100 text-blue align-center block p-4 pt-4.5 pr-0`}
  //         >
  //           info
  //         </span>
  //         <div className="flex flex-col p-3 pl-2 bg-blue-300 w-full">
  //           <div className="font-semibold">
  //             Sorry, the In-Store trade-in option for this pilot ended November
  //             17th 2024.
  //           </div>
  //         </div>
  //       </div>
  //     </div>
  //   </div>
  // );

  return (
    <FormContainer
      submitLabel={t("ui.button_continue")}
      submitHref={`${t("urls.trade_in_checklist")}`}
      submitParams={updatedParams}
      submitDisabled={
        !selectedOption ||
        (parsedWeight >
          weightRestrictions?.find((weight) => weight.unit === "lbs").maxMail &&
          selectedOption === "mail")
      }
      submitTracking={[EVENT_9]}
      secondaryLabel={t("ui.button_back")}
      secondaryHref={`${t("urls.trade_in_weight")}`}
      secondaryParams={updatedParams}
    >
      <FloodlightEvent tag="DC-14832541/pageview/pl_metho+per_session" />
      <ProgressBar
        step={2}
        showLabel
        stepsNum={isKiosk ? (user ? 5 : 6) : user ? 5 : 6}
      />
      <h2 className="font-semibold text-6xl max-w-lg">
        {t("trade-in.method.title")}
      </h2>
      <WeightEstimation
        title={"Your reward will be worth"}
        className="mt-4"
        singleValue={
          !showPayment
            ? localizeDecimalSeparator(estimate.gift, currentLocation)
            : null
        }
        range={
          showPayment
            ? [
                localizeDecimalSeparator(estimate.payment, currentLocation),
                localizeDecimalSeparator(estimate.gift, currentLocation),
                currentLocation,
              ]
            : null
        }
        animate
        locale={currentLocation?.locale}
        description={t("ui.estimate_part2", {
          weight: weightEstimate,
          unit: t("scale.weight"),
        })}
      />

      {parsedWeight > 25 && (
        <InputValidation errorMessage="Your estimated weight is over the 25.00 lbs Mail-In limit. Go back and adjust the weight or choose the take to store option below." />
      )}

      <RadioGroup
        options={selectedRadioOptions}
        name="custom-radio"
        selectedOption={selectedOption}
        onChange={setSelectedOption}
        className="mt-2"
        disabledOptions={["mail"].filter((option) => parsedWeight > 25)}
      />
    </FormContainer>
  );
}
